import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-model/views/default-catalog-products-views";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { matchPath, Navigate } from "react-router-dom";

const catalogProductsViewBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/products",
  "/products/:viewName",
  "/products/:viewName/:productFamilyId",
  "/products/:viewName/:productFamilyId/:productId",
  "/products/:viewName/:productFamilyId/:productId/locations/:locationId",
  "/consignment",
  "/consigned-product-families/:productFamilyId",
];

const CATALOG_ROUTE_BASE = "/stores/:storeId/catalog";

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${CATALOG_ROUTE_BASE}${route}`, url),
  );
}

export const catalogRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "Catalog",
      permission: ({ team }) =>
        team.platform === "shopify" &&
        team.settings.ims?.catalogEnabled === true,
    },
    async lazy() {
      const { Page } = await import("../../page");
      const { ImsStatusWrapper } = await import(
        "@redotech/redo-merchant-inventory-app/components/ims-status-wrapper"
      );
      return {
        element: (
          <ImsStatusWrapper>
            <Page hidePadding={hidePadding} />
          </ImsStatusWrapper>
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="products" /> },
      {
        path: "products",
        handle: { breadcrumb: "Products" },
        children: [
          {
            index: true,
            element: <Navigate replace to={DEFAULT_CATALOG_VIEW} />,
          },
          {
            path: ":viewName",
            handle: { breadcrumb: catalogProductsViewBreadcrumb },
            async lazy() {
              const { CatalogProductsWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/catalog/catalog-products-wrapper"
              );
              return {
                element: (
                  <CatalogProductsWrapper
                    breadcrumb={catalogProductsViewBreadcrumb}
                    productBreadcrumb={productDetailBreadcrumb}
                    productFamilyBreadcrumb={productFamilyDetailBreadcrumb}
                  />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { CatalogProducts } = await import(
                    "@redotech/redo-merchant-inventory-app/catalog-products"
                  );
                  return { Component: CatalogProducts };
                },
              },
              {
                path: ":productFamilyId",
                handle: { breadcrumb: productFamilyDetailBreadcrumb },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { ProductFamilyView } = await import(
                        "@redotech/redo-merchant-inventory-app/product-families/product-family-view"
                      );
                      return { Component: ProductFamilyView };
                    },
                  },
                  {
                    path: ":productId",
                    handle: { breadcrumb: productDetailBreadcrumb },
                    children: [
                      {
                        index: true,
                        async lazy() {
                          const { ProductView } = await import(
                            "@redotech/redo-merchant-inventory-app/products/product-view"
                          );
                          return { Component: ProductView };
                        },
                      },
                      {
                        path: "locations/:locationId",
                        handle: { breadcrumb: "Adjustment history" },
                        async lazy() {
                          const { AdjustmentHistory } = await import(
                            "@redotech/redo-merchant-inventory-app/adjustment-history/adjustment-history"
                          );
                          return { Component: AdjustmentHistory };
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "consigned-product-families/:productFamilyId",
        handle: { breadcrumb: productFamilyDetailBreadcrumb },
        async lazy() {
          const { ProductFamilyView } = await import(
            "@redotech/redo-merchant-inventory-app/product-families/product-family-view"
          );
          return { Component: ProductFamilyView };
        },
      },
      {
        path: "consignment",
        handle: { breadcrumb: "Consignment" },
        async lazy() {
          const { CatalogConsignmentProducts } = await import(
            "@redotech/redo-merchant-inventory-app/catalog-consignment/catalog-consignment-products"
          );
          return { Component: CatalogConsignmentProducts };
        },
      },
    ],
  },
];
